import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { demoMessages, demoResponses, initialResponses, responseTemplates } from './content/SlackMessages';

interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  toolCalls?: ToolCall[];
  toolOutput?: {
    tool: string;
    input: any;
    output: any;
  };
}

interface ToolCall {
  tool: string;
  input?: any;
  output?: any;
  status: 'running' | 'complete' | 'error';
}

interface SlackInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  autoDemo?: boolean;
}

// Helper function to parse Slack-style links
const parseSlackLinks = (text: string): (string | React.ReactElement)[] => {
  // This will store the parts of the message
  const parts: (string | React.ReactElement)[] = [];
  
  // Start from the beginning of the string
  let lastIndex = 0;
  
  // Regular expression to match Slack-style links <url|text>
  // This regex is improved to handle various URL formats
  const linkRegex = /<(https?:\/\/[^|>]+)(?:\|([^>]+))?>/g;
  let match;
  
  // Find all matches
  while ((match = linkRegex.exec(text)) !== null) {
    // Add the text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the link as a React element
    const url = match[1];
    // If there's no pipe character, use the URL as the text
    const linkText = match[2] || url;
    
    parts.push(
      <a 
        key={match.index} 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {linkText}
      </a>
    );
    
    // Update lastIndex
    lastIndex = match.index + match[0].length;
  }
  
  // Add the remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};

// Helper function to render message text with formatting
const renderMessageText = (text: string): React.ReactNode => {
  // First parse links
  const partsWithLinks = parseSlackLinks(text);
  
  // Process each part - some parts are strings, others are already JSX elements
  return partsWithLinks.map((part, index) => {
    // If it's already a JSX element (a link), return it
    if (typeof part !== 'string') {
      return part;
    }
    
    // Process bold text (*bold*)
    const boldParts: (string | React.ReactElement)[] = [];
    let lastBoldIndex = 0;
    const boldRegex = /\*(.*?)\*/g;
    let boldMatch;
    
    while ((boldMatch = boldRegex.exec(part)) !== null) {
      // Add text before the bold
      if (boldMatch.index > lastBoldIndex) {
        boldParts.push(part.substring(lastBoldIndex, boldMatch.index));
      }
      
      // Add the bold text
      boldParts.push(
        <strong key={`bold-${index}-${boldMatch.index}`}>
          {boldMatch[1]}
        </strong>
      );
      
      // Update lastBoldIndex
      lastBoldIndex = boldMatch.index + boldMatch[0].length;
    }
    
    // Add remaining text
    if (lastBoldIndex < part.length) {
      boldParts.push(part.substring(lastBoldIndex));
    }
    
    // Process code/monospace text using backticks
    const processCodeBlocks = (text: string | React.ReactElement): React.ReactNode => {
      if (typeof text !== 'string') {
        return text;
      }
      
      const codeParts: (string | React.ReactElement)[] = [];
      let lastCodeIndex = 0;
      const codeRegex = /`([^`]+)`/g;
      let codeMatch;
      
      while ((codeMatch = codeRegex.exec(text)) !== null) {
        // Add text before the code
        if (codeMatch.index > lastCodeIndex) {
          codeParts.push(text.substring(lastCodeIndex, codeMatch.index));
        }
        
        // Add the monospace/code text
        codeParts.push(
          <code 
            key={`code-${codeMatch.index}`}
            className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono"
          >
            {codeMatch[1]}
          </code>
        );
        
        // Update lastCodeIndex
        lastCodeIndex = codeMatch.index + codeMatch[0].length;
      }
      
      // Add remaining text
      if (lastCodeIndex < text.length) {
        codeParts.push(text.substring(lastCodeIndex));
      }
      
      return codeParts.map((cp, cpIndex) => (
        <React.Fragment key={`code-part-${cpIndex}`}>{cp}</React.Fragment>
      ));
    };
    
    // Return the formatted part with code blocks processed
    return boldParts.map((bp, bpIndex) => (
      <React.Fragment key={`part-${index}-${bpIndex}`}>
        {processCodeBlocks(bp)}
      </React.Fragment>
    ));
  });
};

const SlackInterface: React.FC<SlackInterfaceProps> = ({ initialMessages = [], onSendMessage, autoDemo = true }) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoInProgress, setDemoInProgress] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Record<number, boolean>>({});
  const demoRunningRef = useRef(false);
  const demoLoopCountRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingIndicatorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<Message[]>(messages);
  
  // Loading GIFs from Slack for tool statuses
  const loadingGifs = [
    "https://emojis.slackmojis.com/emojis/images/1711570900/91597/loading.gif?1711570900",
    "https://emojis.slackmojis.com/emojis/images/1643514649/6559/spinthinking.gif?1643514649",
    "https://emojis.slackmojis.com/emojis/images/1678365903/64563/thinking_spin.gif?1678365903",
    "https://emojis.slackmojis.com/emojis/images/1643514702/7043/loading.gif?1643514702",
    "https://emojis.slackmojis.com/emojis/images/1643514723/7281/loading.gif?1643514723",
    "https://emojis.slackmojis.com/emojis/images/1643514453/4360/loading_wheel.gif?1643514453"
  ];

  // Keep messagesRef updated with latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Sample channels and users for Slack sidebar
  const channels = [
    { name: 'general', unread: false, mentions: 0 },
    { name: 'frontend-team', unread: true, mentions: 2 },
    { name: 'backend-team', unread: true, mentions: 0 },
    { name: 'devops-team', unread: false, mentions: 0 },
    { name: 'engineering-all', unread: false, mentions: 0 },
  ];

  const directMessages = [
    { name: 'AgentBot', online: true, unread: false, isBot: true },
    { name: 'Sarah Kim', online: true, unread: false },
    { name: 'David Chen', online: false, unread: true },
    { name: 'Alex Johnson', online: true, unread: false },
    { name: 'Maya Patel', online: false, unread: false },
  ];

  const apps = [
    { name: 'GitHub', description: 'Manage code repositories' },
    { name: 'Jira', description: 'Track issues and projects' },
    { name: 'Jenkins', description: 'CI/CD pipelines' },
    { name: 'PagerDuty', description: 'On-call management' },
  ];

  // Fisher-Yates shuffle algorithm for randomizing message order
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auto demo functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoDemo && demoMessages.length > 0 && !demoComplete && !demoInProgress && !demoRunningRef.current && (messages.length === 0 || demoLoopCountRef.current > 0)) {
      setDemoInProgress(true);
      demoRunningRef.current = true;
      runDemoSequence();
    }
  }, [autoDemo, messages.length, demoComplete, demoInProgress]);
  
  // Disabled exhaustive-deps for this effect because runDemoSequence would create a circular reference

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll when typing indicator appears or disappears
  useEffect(() => {
    scrollToBottom();
  }, [isTyping]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Reduce the extra padding to ensure proper spacing
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight + 30;
    }
  };

  // Function to simulate typing and sending a message
  const simulateTyping = (message: string) => {
    return new Promise<void>((resolve) => {
      // Clear any existing text
      setInputValue('');
      
      // Simulate typing character by character
      let currentCharIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentCharIndex <= message.length) {
          setInputValue(message.substring(0, currentCharIndex));
          currentCharIndex++;
          
          // Adjust height as we type
          if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
          }
        } else {
          clearInterval(typingInterval);
          
          // Small pause after typing before sending
          setTimeout(() => {
            // Call a specific version of send that uses the current inputValue
            handleSendDemoMessage(message);
            resolve();
          }, 500);
        }
      }, 10 + Math.random() * 10); // Fast typing speed for demos
    });
  };

  // Special version of handleSendMessage specifically for the demo
  const handleSendDemoMessage = (demoMessage: string) => {
    if (!demoMessage.trim()) return;
    
    // Create a unique message ID to prevent duplicates
    const messageId = Date.now();
    
    // Create and add the user message
    const userMessage: Message = {
      id: messageId,
      sender: 'user',
      text: demoMessage,
      timestamp: new Date(),
    };
    
    // Only add this message if it doesn't already exist
    setMessages(prev => {
      // Check if this message already exists
      const exists = prev.some(m => m.text === demoMessage && m.sender === 'user');
      if (exists) return prev;
      return [...prev, userMessage];
    });
    
    setInputValue('');
    
    // Simulate agent responding with the predetermined response for this demo message
    setTimeout(() => {
      setIsTyping(true);
    }, 300);
    
    // Simulate agent processing time
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use predefined responses for the demo
      const demoResponse = demoResponses[demoMessage];
      if (!demoResponse) return;
      
      // Get initial response text based on tool type from imported initialResponses
      const initialResponseText = initialResponses[demoResponse.tool] || "I'm working on that request...";
      
      const responseId = Date.now() + 1;
      const botResponse: Message = {
        id: responseId,
        sender: 'agent',
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: demoResponse.tool,
            input: demoResponse.input,
            status: 'running',
          }
        ]
      };
      
      // Only add this message if the user message exists and this bot response doesn't
      setMessages(prev => {
        // Check if we already have this response 
        const userExists = prev.some(m => m.text === demoMessage && m.sender === 'user');
        const botExists = prev.some(m => 
          m.sender === 'agent' && 
          m.text === initialResponseText &&
          m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
        );
        
        if (!userExists || botExists) return prev;
        return [...prev, botResponse];
      });
      
      scrollToBottom();
      
      // Tools always run and complete successfully
      const toolCompletionTime = 2250 + Math.random() * 1500;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          // Find the exact message by comparing content rather than just ID
          const agentMessageIndex = updatedMessages.findIndex(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText &&
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
          );
          
          if (agentMessageIndex !== -1) {
            const agentMessage = updatedMessages[agentMessageIndex];
            if (agentMessage.toolCalls && agentMessage.toolCalls.length > 0) {
              const toolCall = agentMessage.toolCalls[0];
              toolCall.status = 'complete';
              toolCall.output = demoResponse.output;
              
              // Get response template directly without any further processing
              const finalResponse = responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.";
              
              // Update the response text with the template
              agentMessage.text = finalResponse;
              
              // Add tool call data directly to the message
              agentMessage.toolOutput = {
                tool: toolCall.tool,
                input: toolCall.input,
                output: toolCall.output
              };
              
              // Update the message in the array
              updatedMessages[agentMessageIndex] = agentMessage;
            }
          }
          
          return updatedMessages;
        });
        
        scrollToBottom();
      }, toolCompletionTime);
    }, processingTime);
  };

  // Run the full demo sequence
  const runDemoSequence = async () => {
    // Increment loop counter
    demoLoopCountRef.current++;
    console.log(`Starting demo loop ${demoLoopCountRef.current}/1`);
    
    // Wait for initial render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Randomize the order of messages for this loop
    const randomizedMessages = shuffleArray(demoMessages);
    
    // Loop through each demo message
    for (let i = 0; i < randomizedMessages.length; i++) {
      // Wait for previous message to be processed
      if (i > 0) {
        // Wait for agent to finish responding to previous message
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            // Use messagesRef to get the latest messages
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            
            if (lastMessage && lastMessage.sender === 'agent' && 
                (!lastMessage.toolCalls || 
                 lastMessage.toolCalls.every(tc => tc.status === 'complete'))) {
              clearInterval(checkInterval);
              // Add some delay between conversations
              setTimeout(resolve, 5000);
            }
          }, 500);
        });
      }
      
      // Type and send the next message
      await simulateTyping(randomizedMessages[i]);
    }
    
    // Only run a single loop, then mark as complete
    demoRunningRef.current = false;
    setDemoInProgress(false);
    setDemoComplete(true);
    
    // Find the parent component's setActivePlatform function
    // This is dispatched as a custom event since we can't directly access the parent's state
    const switchPlatformEvent = new CustomEvent('switchPlatform', {
      detail: { platform: 'discord' }
    });
    window.dispatchEvent(switchPlatformEvent);
  };

  const handleSendMessage = useCallback(() => {
    if (inputValue.trim() === '') return;

    // Add user message
    const newMessage: Message = {
      id: Date.now(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, newMessage]);
    onSendMessage(inputValue);
    setInputValue('');

    // Simulate agent typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      
      // Randomly select a software dev tool
      const devTools = ['github_search', 'jira_create', 'meeting_schedule', 'deployment_search', 'sprint_report'];
      const selectedTool = devTools[Math.floor(Math.random() * devTools.length)];
      
      // Get initial response text
      let initialResponse = initialResponses[selectedTool] || "I'll help with that!";
      let mockInput = {};
      let mockOutput = {};
      
      // Create mock data for the selected tool
      switch (selectedTool) {
        case 'github_search':
          mockInput = { 
            query: "bug fix", 
            timeframe: "last week",
            repos: ["main-app", "auth-service"]
          };
          mockOutput = { 
            results: [
              { 
                pr_number: "#394", 
                title: "Fix edge case in search algorithm", 
                author: "dev.taylor", 
                status: "merged",
                repo: "main-app",
                merged_at: "Apr 15, 2024",
                comments: 7,
                changes: "+124 -37"
              },
              { 
                pr_number: "#401", 
                title: "Address security vulnerability in user auth", 
                author: "security.team", 
                status: "merged",
                repo: "auth-service",
                merged_at: "Apr 17, 2024",
                comments: 11,
                changes: "+89 -52"
              }
            ],
            branches: ["main", "develop"],
            total_matches: 3
          };
          break;
          
        case 'jira_create':
          mockInput = { 
            summary: "UI rendering issue on mobile devices", 
            description: "Component displays incorrectly on iOS Safari.",
            type: "Bug",
            priority: "High",
            assignee: "frontend-team",
            labels: ["ui", "mobile", "safari"]
          };
          mockOutput = { 
            ticket_id: `UI-${Math.floor(Math.random() * 1000)}`,
            created: true,
            assignee: "frontend-team",
            sprint: "Sprint 45",
            priority: "High",
            status: "To Do",
            link: "https://company.atlassian.net/browse/UI-734",
            watchers: ["design-team", "qa-team"]
          };
          break;
          
        case 'meeting_schedule':
          mockInput = { 
            title: "Feature Planning Session", 
            duration: "45m",
            required_attendees: ["product-manager", "dev-leads"],
            optional_attendees: ["design-team"],
            suggested_times: ["tomorrow 11:00 AM", "tomorrow 3:00 PM"],
            location: "Conference Room A"
          };
          mockOutput = { 
            event_id: `evt_${Math.floor(Math.random() * 100000)}`,
            created: true,
            meeting_link: "https://meet.company.com/feature-planning",
            confirmed_time: "Apr 19, 2024, 11:00 AM - 11:45 AM",
            calendar: "Team Calendar",
            attendees_count: 7,
            meeting_document: "https://docs.company.com/feature-planning-apr19",
            agenda_items: ["Roadmap review", "Feature prioritization", "Resource allocation"]
          };
          break;
          
        case 'deployment_search':
          mockInput = { 
            environment: "staging", 
            count: 3,
            services: ["ui-components", "api-gateway"]
          };
          mockOutput = { 
            deployments: [
              { 
                service: "ui-components", 
                version: "v2.9.4", 
                timestamp: "Apr 17, 2024, 16:22 UTC", 
                deployer: "ci-pipeline", 
                status: "successful",
                commit: "e45df23",
                build_time: "9m 17s"
              },
              { 
                service: "api-gateway", 
                version: "v1.7.2", 
                timestamp: "Apr 16, 2024, 14:05 UTC", 
                deployer: "ci-pipeline", 
                status: "successful",
                commit: "a98c4e7",
                build_time: "7m 33s"
              },
              { 
                service: "ui-components", 
                version: "v2.9.3", 
                timestamp: "Apr 14, 2024, 11:40 UTC", 
                deployer: "ci-pipeline", 
                status: "successful",
                commit: "f12b5d9",
                build_time: "8m 52s"
              }
            ],
            environment_status: "healthy",
            total_services: 5
          };
          break;
          
        case 'sprint_report':
        default:
          mockInput = { 
            sprint: "current", 
            type: "all",
            status: "in-progress"
          };
          mockOutput = { 
            sprint_name: "Sprint 45 (Apr 10 - Apr 24)",
            total_tasks: 37,
            completed: 12,
            in_progress: 18,
            not_started: 7,
            by_type: [
              { name: "Feature", count: 15 },
              { name: "Bug", count: 10 },
              { name: "Technical Debt", count: 8 },
              { name: "Documentation", count: 4 }
            ],
            by_assignee: [
              { name: "frontend-team", count: 13 },
              { name: "backend-team", count: 15 },
              { name: "qa-team", count: 7 },
              { name: "unassigned", count: 2 }
            ],
            burndown_url: "https://company.atlassian.net/sprint-reports/45/burndown",
            velocity: "32 points",
            previous_velocity: "28 points"
          };
          break;
      }
      
      // Create the bot response
      const botResponse: Message = {
        id: Date.now() + 1,
        sender: 'agent',
        text: initialResponse,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: selectedTool,
            input: mockInput,
            status: 'running'
          }
        ]
      };
      
      setMessages(prev => [...prev, botResponse]);
      
      // Simulate tool call completing
      setTimeout(() => {
        setMessages(prev => {
          const updated = [...prev];
          const agentMessageIndex = updated.findIndex(m => 
            m.sender === 'agent' && 
            m.toolCalls?.some(tc => tc.tool === selectedTool && tc.status === 'running')
          );
          
          if (agentMessageIndex !== -1) {
            const agentMessage = updated[agentMessageIndex];
            if (agentMessage.toolCalls && agentMessage.toolCalls.length > 0) {
              const toolCall = agentMessage.toolCalls[0];
              toolCall.status = 'complete';
              toolCall.output = mockOutput;
              
              // Use imported response templates with hardcoded responses
              let finalResponse = "I've completed your request. Here are the results.";
              
              switch (toolCall.tool) {
                case 'github_search':
                  finalResponse = `🔍 *Found 2 relevant PRs:*

• \`PR #394\` *Fix edge case in search algorithm*
  👤 dev.taylor | 📆 Apr 15, 2024 | 📁 main-app
  📝 +124 -37 | 🔗 <https://github.com/company/main-app/pull/394|PR Link>

• \`PR #401\` *Address security vulnerability in user auth*
  👤 security.team | 📆 Apr 17, 2024 | 📁 auth-service
  📝 +89 -52 | 🔗 <https://github.com/company/auth-service/pull/401|PR Link>

Need details on either PR?`;
                  break;
                  
                case 'jira_create':
                  finalResponse = `✅ *${(mockOutput as any).ticket_id} created!*

🐛 *UI rendering issue on mobile devices*
🚨 Priority: High
👥 Assigned: frontend-team
🏃‍♂️ Sprint: Sprint 45
🔗 <https://company.atlassian.net/browse/UI-734|Ticket Link>

Added watchers from design and QA teams. Notify #frontend-team?`;
                  break;
                  
                case 'meeting_schedule':
                  finalResponse = `📅 *Feature Planning Session scheduled!*

🕑 Apr 19, 2024, 11:00 AM - 11:45 AM
🔗 <https://meet.company.com/feature-planning|Meeting Link>
👥 7 team members invited
🔗 <https://docs.company.com/feature-planning-apr19|Document Link>

Calendar invites sent to all participants.`;
                  break;
                  
                case 'deployment_search':
                  finalResponse = `🚀 *Latest Staging Deployments:*

1️⃣ *ui-components* (v2.9.4)
   📆 Apr 17, 2024, 16:22 UTC
   ✅ successful | 🔗 <https://github.com/company/ui-components/commit/e45df23|Commit Link>

2️⃣ *api-gateway* (v1.7.2)
   📆 Apr 16, 2024, 14:05 UTC
   ✅ successful | 🔗 <https://github.com/company/api-gateway/commit/a98c4e7|Commit Link>

3️⃣ *ui-components* (v2.9.3)
   📆 Apr 14, 2024, 11:40 UTC
   ✅ successful | 🔗 <https://github.com/company/ui-components/commit/f12b5d9|Commit Link>

🟢 Environment: healthy | 🔗 <https://staging.company.com/status|Status Dashboard>`;
                  break;
                  
                case 'sprint_report':
                  finalResponse = `🏃‍♂️ *Sprint 45 (Apr 10 - Apr 24) Progress:*

📊 *37 total tasks*
✅ 12 completed | 🔄 18 in progress | ⏳ 7 not started

📋 *By Type:*
🚀 Features: 15
🐛 Bugs: 10
🔧 Tech Debt: 8
📄 Docs: 4

⬆️ Current velocity: 32 points (up from 28)
🔗 <https://company.atlassian.net/sprint-reports/45|Sprint Board>

Need a full progress report?`;
                  break;
              }
              
              // Update the response text with the hardcoded template
              agentMessage.text = finalResponse;
              
              // Add tool call data directly to the message
              agentMessage.toolOutput = {
                tool: toolCall.tool,
                input: toolCall.input,
                output: toolCall.output
              };
            }
          }
          
          return updated;
        });
      }, 3000);
    }, 2250);
  }, [inputValue, onSendMessage]);

  // Handle textarea height adjustment
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle thread expansion
  const toggleThread = (messageId: number) => {
    setExpandedThreads(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex h-[720px] max-w-6xl mx-auto rounded-lg overflow-hidden border border-gray-200 shadow-lg">
      {/* Slack sidebar */}
      <div className="w-60 bg-[#3F0E40] text-white flex flex-col">
        <div className="p-3 border-b border-[#522653] flex items-center justify-between">
          <h2 className="font-bold text-white">DevTeam Hub</h2>
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-[#3F0E40]">
            <span className="text-sm font-bold">DH</span>
          </div>
        </div>
        
        <div className="overflow-y-auto flex-grow">
          <div className="px-3 py-4">
            <div className="mb-3">
              <h3 className="text-xs text-gray-400 uppercase mb-1 font-medium">Channels</h3>
              <ul>
                {channels.map(channel => (
                  <li key={channel.name} className={`flex items-center mb-1 text-xs cursor-pointer ${channel.unread ? 'font-bold' : 'text-gray-300 hover:text-white'}`}>
                    <span className="mr-2">#</span>
                    <span>{channel.name}</span>
                    {channel.mentions > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 rounded-lg">
                        {channel.mentions}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mb-3">
              <h3 className="text-xs text-gray-400 uppercase mb-1 font-medium">Direct Messages</h3>
              <ul>
                {directMessages.map(dm => (
                  <li 
                    key={dm.name} 
                    className={`flex items-center mb-1 text-xs cursor-pointer ${dm.name === 'AgentBot' ? 'bg-[#1164A3] rounded text-white px-1 py-0.5' : dm.unread ? 'font-bold' : 'text-gray-300 hover:text-white'}`}
                  >
                    <span className={`w-2 h-2 rounded-full mr-2 ${dm.online ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                    <span>{dm.name}</span>
                    {dm.isBot && <span className="ml-1 text-xs bg-gray-700 rounded px-1">Bot</span>}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mb-3">
              <h3 className="text-xs text-gray-400 uppercase mb-1 font-medium">Apps</h3>
              <ul>
                {apps.map(app => (
                  <li key={app.name} className="flex items-center mb-1 text-xs text-gray-300 hover:text-white cursor-pointer">
                    <span className="w-4 h-4 bg-gray-600 rounded mr-2"></span>
                    <span>{app.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main chat area */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Channel header */}
        <div className="border-b border-gray-200 px-4 py-2 flex items-center">
          <div className="flex-1">
            <h2 className="font-bold text-sm">AgentBot</h2>
            <p className="text-xs text-gray-500">Your developer assistant</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <button className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Messages area */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-white">
          <div className="space-y-4 pb-2">
            {messages.map((message) => (
              <div key={message.id} className="flex flex-col">
                <div className="flex items-start mb-1">
                  <div className="w-8 h-8 rounded mr-2 overflow-hidden bg-gray-200 flex-shrink-0">
                    {message.sender === 'agent' ? (
                      <div className="w-full h-full bg-[#1164A3] flex items-center justify-center text-white font-bold">
                        AB
                      </div>
                    ) : (
                      <div className="w-full h-full bg-[#E01E5A] flex items-center justify-center text-white font-bold">
                        U
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="font-bold text-xs mr-2">
                        {message.sender === 'agent' ? 'AgentBot' : 'You'}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {formatTimestamp(message.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs mt-1 whitespace-pre-wrap">
                      {renderMessageText(message.text)}
                    </div>
                    
                    {/* Tool calls - only shown for agent messages */}
                    {message.sender === 'agent' && message.toolCalls && message.toolCalls.length > 0 && (
                      <div className="mt-2">
                        {message.toolCalls.map((toolCall, index) => {
                          // Tool-specific emojis
                          const toolEmojis: Record<string, string> = {
                            github_search: '🔍',
                            jira_create: '🐛',
                            meeting_schedule: '📅',
                            deployment_search: '🚀',
                            sprint_report: '📊',
                          };
                          
                          // Status emojis
                          const statusEmojis: Record<string, string> = {
                            running: '🔄',
                            complete: '✅',
                            error: '❌',
                          };
                          
                          const toolEmoji = toolEmojis[toolCall.tool] || '🔧';
                          const statusEmoji = statusEmojis[toolCall.status] || '⚙️';
                          
                          // Format tool name to be more readable
                          const formatToolName = (name: string) => {
                            return name.split('_').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ');
                          };
                          
                          return (
                            <div key={index} className="mb-1 py-1 text-xs text-gray-500">
                              {toolEmoji} <span className="font-medium">{formatToolName(toolCall.tool)}</span> 
                              <span className="mx-1">•</span>
                              <span className="font-medium">
                                {toolCall.status === 'running' ? (
                                  <>
                                    <img 
                                      src={loadingGifs[Math.floor(Math.random() * loadingGifs.length)]} 
                                      alt="loading" 
                                      className="inline w-4 h-4 align-text-bottom mr-1"
                                    />
                                    {toolCall.status}
                                  </>
                                ) : (
                                  <>{statusEmoji} {toolCall.status}</>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Tool Output Thread Button - only shown for agent messages */}
                    {message.sender === 'agent' && message.toolOutput && (
                      <div className="mt-1 flex items-center">
                        <button 
                          onClick={() => toggleThread(message.id)} 
                          className="text-xs text-[#1164A3] hover:text-[#0A4C7F] flex items-center"
                        >
                          <svg 
                            className={`w-3 h-3 mr-1 transition-transform ${expandedThreads[message.id] ? 'rotate-90' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          {expandedThreads[message.id] ? 'Hide details' : 'View details'}
                        </button>
                        <span className="ml-2 text-xs text-gray-500">1 reply</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Thread Reply for Tool Output */}
                {message.sender === 'agent' && message.toolOutput && (
                  <AnimatePresence>
                    {expandedThreads[message.id] && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }} 
                        animate={{ opacity: 1, height: 'auto' }} 
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-10 pl-6 border-l-2 border-gray-200 mt-1 mb-3"
                      >
                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded overflow-hidden bg-gray-100 flex-shrink-0 mr-2">
                            <div className="w-full h-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                              {message.toolOutput.tool.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="font-bold text-xs mr-1">Tool Output</span>
                              <span className="text-gray-500 text-xs">
                                {formatTimestamp(new Date(message.timestamp.getTime() + 1000))}
                              </span>
                            </div>
                            <div className="text-xs py-2">
                              <div className="bg-gray-50 rounded border border-gray-200 p-2 font-mono overflow-x-auto">
                                <div className="font-medium mb-1 text-xs">🔧 Input:</div>
                                <pre className="text-gray-600 mb-2 whitespace-pre-wrap text-xs">
                                  {JSON.stringify(message.toolOutput.input, null, 2)}
                                </pre>
                                <div className="font-medium mb-1 text-xs">📤 Output:</div>
                                <pre className="text-gray-600 whitespace-pre-wrap text-xs">
                                  {JSON.stringify(message.toolOutput.output, null, 2)}
                                </pre>
                              </div>
                              <div className="mt-2">
                                <button 
                                  onClick={() => toggleThread(message.id)} 
                                  className="text-xs text-[#1164A3] hover:text-[#0A4C7F] flex items-center"
                                >
                                  <svg 
                                    className="w-3 h-3 mr-1" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                  Hide details
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div ref={typingIndicatorRef} className="flex items-center mb-2">
                <div className="w-8 h-8 rounded mr-2 overflow-hidden bg-gray-200 flex-shrink-0">
                  <div className="w-full h-full bg-[#1164A3] flex items-center justify-center text-white font-bold">
                    AB
                  </div>
                </div>
                <div className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                  AgentBot is typing...
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* Input area */}
        <div className="border-t border-gray-200 p-3 bg-white">
          <div className="relative rounded-lg border border-gray-300 focus-within:border-blue-500 bg-white overflow-hidden">
            <div className="flex items-center">
              <textarea
                ref={inputRef}
                className="flex-1 px-3 py-2 outline-none resize-none text-xs max-h-36"
                placeholder="Message AgentBot"
                value={inputValue}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                rows={1}
              />
            </div>
            <div className="flex items-center p-2 border-t border-gray-200">
              <div className="flex space-x-2">
                <button className="text-gray-500 hover:text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </button>
                <button className="text-gray-500 hover:text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <button className="text-gray-500 hover:text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <button 
                onClick={handleSendMessage}
                disabled={inputValue.trim() === ''}
                className={`ml-auto px-2 py-1 rounded flex items-center justify-center ${inputValue.trim() === '' ? 'text-gray-400' : 'text-[#1164A3] hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlackInterface;